/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr';
import { addMilliseconds } from 'date-fns';
import LabelledInputLayout from './LabelledInputLayout';
import {
  calculateMinTimeOffsetInMilliseconds,
  convertTimeStampToEtDateTime,
  convertDateTimeToUSLocaleStrCustom,
  getNowInEasternTimeLocal,
  getNowDateTimeInET,
  isFuturePublish,
} from '../helpers/timeHelpers';

import { nextIconString, prevIconString } from '../helpers/flatPickrIconStrings';

const PublishDateInputField = ({
  isPaid = false,
  value,
  onChange,
}) => {
  const nowInETLocal = getNowInEasternTimeLocal();
  // The # of ms until a 5-minute interval of the hour at least 5 minutes from now
  // E.g., if it's 9:03, the next available time is 9:10
  const minTimeOffsetInMilliseconds = calculateMinTimeOffsetInMilliseconds(nowInETLocal, 5);

  const selectedDateTime = convertTimeStampToEtDateTime(value);

  const nowDateTimeInET = getNowDateTimeInET();
  const isScheduled = selectedDateTime && selectedDateTime > nowDateTimeInET;
  let scheduledDateStr = '';
  if (isScheduled) {
    scheduledDateStr = convertDateTimeToUSLocaleStrCustom(selectedDateTime);
  }

  const id = 'publish_date';
  const fpRef = useRef(null);
  const buttonRef = useRef(null);

  const labelText = isPaid ? 'Scheduled to publish' : 'Schedule publishing';
  const helpText = isPaid ? null : 'Select a specific future date and time to publish your press release.';
  const isRequired = isPaid && isFuturePublish(value);

  return (
    <>
      <div className="publish-date-container" style={{ paddingBottom: isPaid ? '0' : '2rem' }}>
        <LabelledInputLayout id={id} label={labelText} isRequired={isRequired}>
          {helpText && <p className="publish-date-description">{helpText}</p>}
          <div className="date-picker-wrapper" data-input data-toggle>
            <Flatpickr
              ref={fpRef}
              data-enable-time
              className="datepicker"
              value={value || null}
              // eslint-disable-next-line object-curly-newline
              options={{
                // format the date that gets written into the input (that will be sent in POST)
                dateFormat: 'Y-m-d H:i',
                // don't let the user select a datetime less than 5 minutes from now (in ET)
                minDate: addMilliseconds(nowInETLocal, minTimeOffsetInMilliseconds),
                // allow time input
                enableTime: true,
                // we need to tell the datepicker that it should be displaying values based off of
                // now in Eastern Time
                // this is a hidden config option that's not found in the docs
                now: nowInETLocal,
                defaultHour: 9,
                defaultMinute: 0,
                time_24hr: false,
                wrap: true,
                disableMobile: true,
                plugins: [
                  new ShortcutButtonsPlugin({
                    button: {
                      label: 'Clear',
                    },
                    onClick: (index, fp) => {
                      fp.clear();
                      fp.close();
                    },
                  }),
                ],
                nextArrow: nextIconString,
                prevArrow: prevIconString,
              }}
              onClose={() => {
                if (isScheduled) {
                  buttonRef.current.focus();
                }
              }}
              onChange={(selectedDates, dateStr, instance) => {
                // If a past date was selected, then clear the datetime selection
                const rightNow = getNowInEasternTimeLocal();
                if (selectedDates[0] < rightNow) {
                  instance.clear();
                  instance.close();
                  onChange(null);
                } else {
                  onChange(dateStr || null);
                }
              }}
              onReady={(selectedDates, dateStr, instance) => {
                // If a past date was selected, then clear the datetime selection
                if (dateStr) {
                  if (selectedDates[0] < getNowInEasternTimeLocal()) {
                    instance.clear();
                    instance.close();
                    onChange(null);
                  } else {
                    onChange(dateStr || null);
                  }
                }
              }}
            >
              <button
                ref={buttonRef}
                type="button"
                className={`scheduled-publish${isPaid ? '--edit' : ''} ${isScheduled ? '' : 'hidden'}`}
                onClick={() => fpRef.current.flatpickr.open()}
              >
                {!isPaid && <span className="scheduled-publish__message">Scheduled to publish on</span>}
                <span className="scheduled-publish__date">{scheduledDateStr}</span>
              </button>
              <input
                aria-labelledby={`${id}_label`}
                type="text"
                className={`datepicker flatpickr-input${isScheduled ? ' hidden' : ''}`}
                placeholder="MM / DD / YYYY --:-- -- ET"
                title="Select a date and time"
                data-input
              />
            </Flatpickr>
            {(!selectedDateTime || selectedDateTime <= nowDateTimeInET) && (
              <span id="publish_date_label" className="message-inline message-inline--warning">
                Scheduled to publish immediately
              </span>
            )}
          </div>
        </LabelledInputLayout>
      </div>
    </>
  );
};

PublishDateInputField.propTypes = {
  isPaid: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default PublishDateInputField;
